import streamSaver from 'streamsaver';
import * as zip from '@zip.js/zip.js';
import { Modal } from 'antd';
import { DownloadingState, useStore } from '~src/store';
import { appLog } from '~src/utils/log';
import { buildFolderWithPathStructure } from '~src/utils/downloader/shared';
import {
  newTenderShareFolderDownload,
  getTenderShareFolderFilesURLs,
  loadSharedTenderSubfolders,
} from '~src/services/api/shares';
import type { DbTenderFolder } from '~src/types/db';

class ZipStream {
  private zipWriter: zip.ZipWriter<WritableStream<any>> | null = null;
  private writableStream: WritableStream<any> | null = null;
  private successLog: string[] = [];
  public errorLog: string[] = [];
  private currentFileAlreadyStreamed = 0;
  private totalFiles = 0;
  private totalSize = 0;
  private startTime = 0;
  public endTime = 0;
  public fileMap: Map<string, number> = new Map();
  public isAborted = false;

  constructor(totalFiles: number, totalSize: number) {
    this.totalFiles = totalFiles;
    this.totalSize = totalSize;
    useStore.getState().changeTotalFileSize(totalSize);
  }

  start(zipName: string) {
    try {
      this.startTime = performance.now();
      this.writableStream = streamSaver.createWriteStream(zipName);
      this.zipWriter = new zip.ZipWriter(this.writableStream);
    } catch (error) {
      this.isAborted = true;
    }
  }

  private getUniqueFileName(filePath: string): string {
    let uniquePath = filePath;
    let count = 1;
    const match = filePath.match(/^(.*?)(\(\d+\))?(\.[^/.]+)?$/);
    const baseName = match?.[1] || filePath;
    const extension = match?.[3] || '';

    while (this.fileMap.has(uniquePath)) {
      uniquePath = `${baseName}(${count})${extension}`;
      count++;
    }

    this.fileMap.set(uniquePath, count);
    return uniquePath;
  }

  async addFile(fullPath: string, blob: Blob) {
    if (this.isAborted || !this.zipWriter) return;

    const uniquePath = this.getUniqueFileName(fullPath);
    try {
      useStore.getState().changeDownloadingState(DownloadingState.FETCHING);
      await this.zipWriter.add(uniquePath, new zip.BlobReader(blob));
      this.currentFileAlreadyStreamed++;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);
      this.successLog.push(
        `${uniquePath} - success (${(blob.size / 1024 / 1024).toFixed(2)} MB)`
      );
    } catch (error) {
      this.errorLog.push(
        `${uniquePath} (0.00 MB) - ERROR: ${error?.message} | D: Folder`
      );
      useStore
        .getState()
        .addError(
          `${uniquePath} (0.00 MB) - ERROR: ${error?.message} | D: Folder`
        );
      this.isAborted = true;
    }
  }

  async addEmptyFolder(folderPath: string) {
    if (this.isAborted || !this.zipWriter) return;
    try {
      const uniqueFolderPath = folderPath.endsWith('/')
        ? folderPath
        : folderPath + '/';

      await this.zipWriter.add(uniqueFolderPath, new zip.TextReader(''));

      this.fileMap.set(uniqueFolderPath, 1);
    } catch (error) {
      this.errorLog.push(
        `${folderPath} - ERROR: Failed to create empty folder`
      );
    }
  }

  async finish() {
    try {
      if (!this.zipWriter || this.isAborted) return;
      if (this.endTime !== 0) {
        return;
      }

      this.endTime = performance.now();
      const totalSizeMB = (this.totalSize / 1024 / 1024).toFixed(2);
      const timeTaken = (this.endTime - this.startTime) / 1000;
      const speed = (this.totalSize / 1024 / 1024 / timeTaken).toFixed(2);
      const totalErrors = this.errorLog.length;

      const mergedLogs: string[] = [];
      const successEntries = [...this.successLog];
      const errorEntries = [...this.errorLog];
      while (successEntries.length || errorEntries.length) {
        if (successEntries.length) mergedLogs.push(successEntries.shift()!);
        if (errorEntries.length) mergedLogs.push(errorEntries.shift()!);
      }

      const finalLog = [
        ...mergedLogs,
        `\n==== Summary V2 ====`,
        `Total files - ${this.totalFiles}`,
        `Total errors - ${totalErrors}`,
        `Total size - ${totalSizeMB} MB`,
        `Download speed - ${speed} MB/s`,
        `Time taken - ${timeTaken.toFixed(2)} s`,
      ];
      if (totalErrors > 0) {
        finalLog.push(`\n==== ${totalErrors} Errors ====`, ...this.errorLog);
      }

      const logBlob = new Blob([finalLog.join('\n')], { type: 'text/plain' });
      if (!this.fileMap.has('download_log.txt')) {
        await this.zipWriter.add(
          'download_log.txt',
          new zip.BlobReader(logBlob)
        );
        this.fileMap.set('download_log.txt', 1);
      }

      await this.zipWriter.close();

      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    } catch (error) {
      this.isAborted = true;
    }
  }
}

const createWorker = () => {
  return new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `self.onmessage = async (event) => {
            const { file } = event.data;
            try {
              const response = await fetch(file.url);
              if (!response.ok) {
                throw new Error(\`HTTP \${response.status} - \${response.statusText}\`);
              }
              const blob = await response.blob();
              self.postMessage({ success: true, file, blob });
            } catch (error) {
              console.error(\`Worker Error - File: \${file.file_name}, Error: \${error.message}\`);
              self.postMessage({ success: false, file, error: error.message });
            }
          };`,
        ],
        { type: 'application/javascript' }
      )
    )
  );
};

const ModalConfirmDownload = async () => {
  return new Promise<boolean>((resolve) => {
    Modal.confirm({
      title: 'Large folder download',
      content:
        'This folder contains a lot of files and may take a long time. Continue?',
      okText: 'Download',
      cancelText: 'Cancel',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
};

const findAllEmptyFoldersWithPaths = (
  folderId: number | string,
  allFolders: (DbTenderFolder | any)[],
  rootFolderName: string
) => {
  const emptyFolders: { name: string; path: string }[] = [];

  const stack = [{ id: folderId, path: rootFolderName }];
  while (stack.length) {
    const poppedItem = stack.pop();
    if (!poppedItem) continue;
    const { id: currentId, path: currentPath } = poppedItem;
    const children = allFolders.filter(
      (folder) => folder.parent_id === currentId
    );
    let hasFiles = false;
    for (const child of children) {
      const childPath = `${currentPath}/${child.name}`;
      if (child.is_empty) {
        emptyFolders.push({ name: child.name, path: childPath });
      } else {
        hasFiles = true;
      }
      stack.push({ id: child.id, path: childPath });
    }

    if (!hasFiles && !emptyFolders.some((f) => f.path === currentPath)) {
      emptyFolders.push({
        name: currentPath.split('/').pop() || '',
        path: currentPath,
      });
    }
  }

  return emptyFolders;
};

// -------------------------------
//  Recursively load subfolders (Crucial for ROOT download)
// -------------------------------

const uniqFoldersById = (folders: DbTenderFolder[]) => {
  const map = new Map<string, DbTenderFolder>();
  for (const f of folders) map.set(String(f.id), f);
  return Array.from(map.values());
};

// The API endpoint GET /tender_shares/{share_id}/tender_folders/{root_folder_id}/folders
// returns ALL descendant subfolders of a root folder in one flat call (parent_id allows
// local tree building). root_folder_id MUST be a true root-level folder id – calling
// it with a non-root id returns 400 from TenderRootFolderCheckMiddleware.
// Therefore: one call with the correct root id, no recursion needed.
const loadAllSharedSubfolders = async ({
  shareId,
  shareKey,
  rootFolderId,
}: {
  shareId: string;
  shareKey: string;
  rootFolderId: number;
}): Promise<DbTenderFolder[]> => {
  try {
    const children = await loadSharedTenderSubfolders(
      ['shared-subfolders', shareId, rootFolderId],
      { share_id: shareId, share_key: shareKey, root_folder_id: rootFolderId }
    );
    return uniqFoldersById(children || []);
  } catch (e) {
    return [];
  }
};

export const downloadTenderShareFolder = async ({
  folder,
  allFolders,
  shareId,
  shareKey,
}: {
  folder: DbTenderFolder;
  allFolders: DbTenderFolder[];
  shareId: string;
  shareKey: string;
}): Promise<void> => {
  try {
    const folderId = folder.id;
    const rootFolderName = folder.name;

    // ---------------------------------------------
    // 1. Prepare Full Folder Tree
    // ---------------------------------------------

    // 1) Derive root folders locally.
    //    GET /tender_shares/{share_id}/tender_folders is currently broken on the
    //    server (TenderRootFolderCheckMiddleware returns 400). We don't need it –
    //    we can identify roots from the locally-known tree (allFolders + folder).
    let sharedRootFolders: DbTenderFolder[] = [];
    {
      const localTree = uniqFoldersById([...(allFolders || []), folder]);
      const idSet = new Set(localTree.map((f) => String(f.id)));
      const candidates = localTree.filter(
        (f) => !f.parent_id || !idSet.has(String(f.parent_id))
      );
      sharedRootFolders = candidates.length ? candidates : [folder];
    }

    // 2) Load all descendants with a single API call.
    //    Find the true root folder id by walking up from folder through the known tree.
    //    The API requires root_folder_id to be a root-level folder id.
    const allKnownForRoot = uniqFoldersById([
      ...(allFolders || []),
      ...sharedRootFolders,
      folder,
    ]);
    const computedRootFolderId = (() => {
      if (sharedRootFolders.some((r) => String(r.id) === String(folderId))) {
        return Number(folderId);
      }
      let current = allKnownForRoot.find(
        (f) => String(f.id) === String(folderId)
      );
      while (current && current.parent_id != null) {
        const parent = allKnownForRoot.find(
          (f) => String(f.id) === String(current!.parent_id)
        );
        if (!parent) break;
        if (sharedRootFolders.some((r) => String(r.id) === String(parent.id))) {
          return Number(parent.id);
        }
        current = parent;
      }
      return Number(folderId);
    })();

    // Only call the subfolder API if computedRootFolderId is a provable root
    // (parent_id == null in the local tree). Otherwise the server's
    // TenderRootFolderCheckMiddleware returns 400, and we fall back to the
    // locally-known tree as the descendants source.
    const computedRootFolder = allKnownForRoot.find(
      (f) => String(f.id) === String(computedRootFolderId)
    );
    const isProvableRoot =
      computedRootFolder != null && computedRootFolder.parent_id == null;

    const sharedSubfoldersRecursive = isProvableRoot
      ? await loadAllSharedSubfolders({
          shareId,
          shareKey,
          rootFolderId: computedRootFolderId,
        })
      : allFolders || [];

    // 3) Merge everything into one tree list
    const allFoldersFull = uniqFoldersById([
      ...(allFolders || []),
      ...(sharedRootFolders || []),
      ...(sharedSubfoldersRecursive || []),
      folder, // safety
    ]);

    // build folders hierarchy from full tree
    const structure = buildFolderWithPathStructure(folder, allFoldersFull);

    // Create folders from structure paths
    const folderPathsToCreate = uniqFoldersById(
      structure.map((s: any) => ({ id: s.id, name: s.path } as any))
    )
      .map((x: any) => x.name)
      .filter(Boolean);

    // Ensure root folder always exists
    const allPathsSet = new Set<string>(folderPathsToCreate);
    if (!allPathsSet.has(rootFolderName)) {
      folderPathsToCreate.unshift(rootFolderName);
    }

    // console.log(`structure folder`, {
    //   structure,
    //   allFolders: allFoldersFull,
    //   folder,
    // });

    // ---------------------------------------------
    // 2. Prepare Files
    // ---------------------------------------------
    const files = await newTenderShareFolderDownload('files', {
      shareId,
      shareKey,
      rootFolderId: computedRootFolderId,
      folderId: folder.id,
    });

    let startDownload = true;

    if (files.length > 500) {
      startDownload = await ModalConfirmDownload();
    }

    if (!startDownload) {
      return;
    }

    const totalSize = files.reduce((acc, file) => acc + file.file_size, 0);

    const zipStream = new ZipStream(files.length, totalSize);
    useStore.getState().totalDownloadFiles(files.length);
    zipStream.start(`C-WEB-Share-${Date.now()}.zip`);

    if (zipStream.isAborted) {
      return;
    }

    // create all folder paths before files
    for (const p of folderPathsToCreate) {
      await zipStream.addEmptyFolder(p);
    }

    if (!files.length) {
      await zipStream.finish();
      return;
    }

    // ---------------------------------------------
    // 3. Worker Queue
    // ---------------------------------------------
    const workers: Worker[] = [];
    const fileQueue = [...files];
    let activeWorkers = 0;
    const NUM_WORKERS = 5;

    for (let i = 0; i < NUM_WORKERS; i++) {
      workers.push(createWorker());
    }

    const processFile = async (file: any) => {
      // Pass computedRootFolderId so TenderRootFolderCheckMiddleware is satisfied.
      // VFDownloadUrlUsingKeyVM returns { key, url } – match on 'key'.
      const urls = await getTenderShareFolderFilesURLs(
        shareId,
        shareKey,
        [file.using_key],
        computedRootFolderId
      );

      const urlData =
        (urls || []).find(
          (u: any) => String(u?.key) === String(file.using_key)
        ) || (urls || [])[0];

      const fileURL = (urlData as any)?.url;

      if (!fileURL) {
        const msg = `${file.file_name} - ERROR: Missing URL (using_key=${file.using_key}, folder_id=${file.folder_id})`;
        zipStream.errorLog.push(msg);
        useStore.getState().addError(msg);
        return null;
      }

      return { ...file, url: fileURL };
    };

    const finishIfDone = async () => {
      if (fileQueue.length === 0 && activeWorkers === 0) {
        setTimeout(async () => {
          if (fileQueue.length === 0 && activeWorkers === 0) {
            await zipStream.finish();
            workers.forEach((w) => w.terminate());
          }
        }, 200);
      }
    };

    const assignFileToWorker = async (worker: Worker) => {
      if (zipStream.isAborted) return;

      const nextFile = fileQueue.shift();
      if (!nextFile) {
        await finishIfDone();
        return;
      }

      activeWorkers++;

      const processedFile = await processFile(nextFile);

      if (!processedFile) {
        activeWorkers--;
        if (fileQueue.length > 0) {
          await assignFileToWorker(worker);
        } else {
          await finishIfDone();
        }
        return;
      }

      // Check if worker is still valid before posting (rare edge case, but safe)
      if (worker) {
        worker.postMessage({ file: processedFile });
      } else {
        activeWorkers--;
        // Re-queue file if worker failed strangely
        fileQueue.unshift(nextFile);
      }
    };

    // Attach event listeners ONCE per worker
    for (const worker of workers) {
      worker.onmessage = async (event) => {
        try {
          const { success, file, blob, error } = event.data;
          if (!file) return;

          const fileFolderId = (file as any).folder_id;

          const folderPathFromStructure =
            structure.find((f: any) => {
              if (fileFolderId == null) return false;
              return (
                String(f.id) === String(fileFolderId) ||
                (f.using_key != null &&
                  String(f.using_key) === String(fileFolderId))
              );
            })?.path || '';

          const safeFolderPath =
            folderPathFromStructure || rootFolderName || '';

          const fullPath = safeFolderPath
            ? `${safeFolderPath}/${file.file_name}`
            : file.file_name;

          if (success) {
            await zipStream.addFile(fullPath, blob);
          } else {
            zipStream.errorLog.push(`${fullPath} - ERROR: ${error}`);
            useStore.getState().addError(`${fullPath} - ERROR: ${error}`);
          }
        } catch (err) {
          zipStream.errorLog.push(`Worker processing error - ${err.message}`);
          useStore
            .getState()
            .addError(`Worker processing error - ${err.message}`);
        } finally {
          activeWorkers--;
          if (fileQueue.length > 0) {
            await assignFileToWorker(worker);
          } else {
            await finishIfDone();
          }
        }
      };

      worker.onerror = async (e) => {
        zipStream.errorLog.push(`Worker error - ${e.message}`);
        useStore.getState().addError(`Worker error - ${e.message}`);
        activeWorkers--;
        await finishIfDone();
      };
    }

    // Kick off workers
    for (const worker of workers) {
      await assignFileToWorker(worker);
    }
  } catch (err) {
    appLog('downloadTenderShareFolder -> err', err);
  }
};
